import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Search, AlertTriangle } from 'lucide-react';

import { useMultiSearchMutation } from '@/services/qriotsApi';
import {
  MAX_MULTI_SEARCH_ITEMS,
  type GameCode,
  type MultiSearchItem,
  type MultiSearchResultEntry,
  type Platform,
} from '@/types/qriots';
import { Loader } from '@/components/common/Loader';

const ALL_PLATFORMS: Platform[] = [
  'EUW1', 'EUN1', 'NA1', 'KR', 'JP1', 'BR1', 'LA1', 'LA2',
  'OC1', 'TR1', 'RU', 'ME1', 'PH2', 'SG2', 'TH2', 'TW2', 'VN2',
];

interface ParsedLine {
  raw: string;
  gameName?: string;
  tagLine?: string;
  invalid?: string;
}

const TAGLINE_RE = /^[A-Za-z0-9]{3,5}$/;

function parseLines(text: string): ParsedLine[] {
  return text
    .split(/[\n,]+/)
    .map((l) => l.trim())
    .filter(Boolean)
    .slice(0, MAX_MULTI_SEARCH_ITEMS)
    .map((raw): ParsedLine => {
      const hashIdx = raw.indexOf('#');
      if (hashIdx === -1) return { raw, invalid: 'no_hash' };
      const gameName = raw.slice(0, hashIdx).trim();
      const tagLine = raw.slice(hashIdx + 1).trim();
      if (!gameName) return { raw, invalid: 'no_name' };
      if (!TAGLINE_RE.test(tagLine)) return { raw, invalid: 'bad_tag' };
      return { raw, gameName, tagLine };
    });
}

const ERROR_KEY_MAP = {
  not_found: 'multiSearch.error.not_found',
  rate_limit: 'multiSearch.error.rate_limit',
  riot_error: 'multiSearch.error.riot_error',
  invalid_input: 'multiSearch.error.invalid_input',
  unknown: 'multiSearch.error.unknown',
} as const;

function ResultCard({ entry }: { entry: MultiSearchResultEntry }) {
  const { t } = useTranslation('riot');
  if (entry.profile) {
    const p = entry.profile;
    const solo = p.ranks.soloDuo;
    const top = p.topMastery?.[0];
    return (
      <Link
        to={`/riot/lol/${encodeURIComponent(p.puuid)}?region=${p.region}&gameName=${encodeURIComponent(p.riotId.gameName)}&tagLine=${encodeURIComponent(p.riotId.tagLine)}`}
        className="flex flex-col gap-1 rounded-lg border border-success/30 bg-bg-secondary p-3 active:opacity-80"
      >
        <p className="truncate text-sm font-bold text-fg">
          {p.riotId.gameName}
          <span className="text-fg-muted">#{p.riotId.tagLine}</span>
        </p>
        <p className="text-xs text-fg-muted">
          {p.region}
          {p.summonerLevel ? ` · ${t('profile.levelShort', { level: p.summonerLevel })}` : ''}
          {solo ? ` · ${solo.tier} ${solo.division} · ${solo.lp} LP` : ` · ${t('title.notRanked')}`}
        </p>
        {top && (
          <p className="truncate text-xs text-fg-muted">
            🏆 {top.championName ?? t('champion.unknown', { id: top.championId })} · M{top.level}
          </p>
        )}
      </Link>
    );
  }
  const errKey = entry.error?.code ?? 'unknown';
  const errLabelKey = ERROR_KEY_MAP[errKey];
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-border bg-bg-tertiary p-3">
      <p className="flex items-center gap-1.5 truncate text-sm font-bold text-fg">
        <AlertTriangle aria-hidden className="h-4 w-4 text-danger" />
        {entry.riotId.gameName}
        <span className="text-fg-muted">#{entry.riotId.tagLine}</span>
      </p>
      <p className="text-xs text-fg-muted">{t(errLabelKey)}</p>
    </div>
  );
}

export function RiotMultiSearch() {
  const { t } = useTranslation('riot');
  const [text, setText] = useState('');
  const [region, setRegion] = useState<Platform>('EUW1');
  const [game, setGame] = useState<GameCode>('lol');
  const [run, mutation] = useMultiSearchMutation();
  const isLoading: boolean = mutation.isLoading;
  const error = mutation.error;
  const data = mutation.data;

  const parsed = useMemo(() => parseLines(text), [text]);
  const validParsed = parsed.filter((p) => p.gameName && p.tagLine);
  const canSubmit = validParsed.length > 0 && !isLoading;

  const onSubmit = () => {
    if (!canSubmit) return;
    const items: MultiSearchItem[] = validParsed.map((p) => ({
      gameName: p.gameName!,
      tagLine: p.tagLine!,
      region,
    }));
    run({ game, items });
  };

  return (
    <div className="flex flex-col gap-4 pb-6 pt-4">
      <header className="mx-4 flex flex-col gap-1">
        <h1 className="text-xl font-bold text-fg">{t('multiSearch.title')}</h1>
        <p className="text-sm text-fg-muted">{t('multiSearch.subtitle')}</p>
      </header>

      <div className="mx-4 flex flex-col gap-3 rounded-xl bg-bg-secondary p-4">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-fg-muted">
            {t('multiSearch.input')}
          </span>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('multiSearch.placeholder')}
            rows={5}
            className="rounded-lg border border-border bg-bg-tertiary px-3 py-2 text-sm text-fg placeholder:text-fg-muted focus:border-brand focus:outline-none"
          />
          <span className="text-[10px] text-fg-muted">
            {String(t('multiSearch.hint', { max: MAX_MULTI_SEARCH_ITEMS, count: validParsed.length }))}
          </span>
        </label>

        <div className="flex gap-2">
          <label className="flex flex-1 flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-fg-muted">
              {t('multiSearch.game')}
            </span>
            <select
              value={game}
              onChange={(e) => setGame(e.target.value as GameCode)}
              className="rounded-lg border border-border bg-bg-tertiary px-2 py-2 text-sm text-fg focus:border-brand focus:outline-none"
            >
              <option value="lol">LoL</option>
              <option value="tft">TFT</option>
              <option value="lor">LoR</option>
            </select>
          </label>
          <label className="flex flex-1 flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-fg-muted">
              {t('multiSearch.region')}
            </span>
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value as Platform)}
              className="rounded-lg border border-border bg-bg-tertiary px-2 py-2 text-sm text-fg focus:border-brand focus:outline-none"
            >
              {ALL_PLATFORMS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </label>
        </div>

        <button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit}
          className="flex items-center justify-center gap-2 rounded-lg bg-success/15 px-3 py-2.5 text-sm font-medium text-fg active:opacity-80 disabled:opacity-50"
        >
          <Search aria-hidden className="h-4 w-4" />
          {String(t('multiSearch.cta', { count: validParsed.length }))}
        </button>
      </div>

      {isLoading && (
        <div className="flex h-32 items-center justify-center">
          <Loader />
        </div>
      )}

      {!!error && !isLoading && (
        <div className="mx-4 rounded-lg border border-danger/30 bg-bg-tertiary p-3 text-sm text-fg">
          {t('multiSearch.error.request')}
        </div>
      )}

      {data && !isLoading && (
        <div className="flex flex-col gap-3">
          <p className="mx-4 text-xs text-fg-muted">
            {String(t('multiSearch.summary', {
              total: data.count,
              ok: data.successCount,
              fail: data.failureCount,
            }))}
          </p>
          <div className="mx-4 grid gap-2">
            {data.results.map((r, i) => (
              <ResultCard key={`${r.riotId.gameName}-${r.riotId.tagLine}-${i}`} entry={r} />
            ))}
          </div>
          <p className="mx-4 text-[10px] leading-relaxed text-fg-muted">
            {data.disclaimer}
          </p>
        </div>
      )}
    </div>
  );
}
